import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import type { CSSProperties } from "react";

// null when baseline is 0 — dividing by zero produces a meaningless percentage,
// so the pill shows "—" instead (see DeltaPill).
function pctChange(current: number, baseline: number): number | null {
  if (baseline === 0) return null;
  return ((current - baseline) / baseline) * 100;
}

function DeltaPill({ pct, invert }: { pct: number | null; invert?: boolean }) {
  if (pct === null) {
    return <span className="text-[11px] text-gray-300">—</span>;
  }
  const up = pct >= 0;
  // For metrics where rising is bad (Block-offs, Available time) the color
  // meaning flips: dropping is green, rising is red. The arrow direction still
  // reflects the actual sign of the change either way.
  const good = invert ? !up : up;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-[1px] text-[11px] font-bold tabular-nums"
      style={{
        background: good
          ? "color-mix(in srgb, var(--perf-green) 18%, white)"
          : "color-mix(in srgb, var(--perf-red) 18%, white)",
        color: good ? "var(--perf-green-dark)" : "var(--perf-red-dark)",
        // A thin outline so the pill still reads on a same-tinted card
        // background (e.g. the green "Pets seen" card) instead of blending in.
        border: "1px solid rgba(0, 0, 0, 0.12)",
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.06)",
      }}
    >
      <Icon className="h-3 w-3" />
      {up ? "+" : ""}
      {pct.toFixed(1)}%
    </span>
  );
}

interface MetricDeltaRowProps {
  label: string;
  current: number;
  baseline: number;
  unit: string;
  textStyle?: CSSProperties;
  // Overrides the default `${n.toFixed(1)} ${unit}` rendering — e.g. currency
  // formatting for revenue cards. Always called with a non-negative number;
  // the sign is prepended separately so formatters never need to handle it.
  formatValue?: (n: number) => string;
  // Flips the pill's green/red meaning — for metrics where a rise is the bad
  // outcome (Block-offs, Available time), not the pct sign itself.
  invert?: boolean;
  // Narrows the fixed label column from 68px to 30px, for callers that cannot
  // afford it. Opt-in: 68px is sized for "vs. NVA avg", and the Utilization and
  // Capacity cards have the width to spare. The Revenue tab's five-across row
  // does not -- measured, its worst row needed 212px of the 185px it gets, and
  // 38px of that gap is this column holding a label that renders 34px wide.
  compactLabel?: boolean;
}

// vs. PY / vs. NVA row: baseline value + absolute diff (both inherit the card's
// own accent color) plus a pct-change pill, whose color is fixed regardless of
// card accent since it carries its own up/down meaning.
//
// All three figures need ~217px, which is why the card grids break to fewer
// columns before a card can get narrower than that (see RevenueTab). The row
// cannot shrink to fit -- flex children with no shrink allowance overlap rather
// than wrap, which is exactly how this row used to collide -- so the column
// count is what has to give, and the breakpoints are load-bearing rather than
// cosmetic.
export function MetricDeltaRow({
  label,
  current,
  baseline,
  unit,
  textStyle,
  formatValue,
  invert,
  compactLabel,
}: MetricDeltaRowProps) {
  const diff = current - baseline;
  const sign = diff >= 0 ? "+" : "-";
  const pct = pctChange(current, baseline);

  const renderValue = (n: number) => (formatValue ? formatValue(n) : `${n.toFixed(1)} ${unit}`);

  return (
    <div className="flex items-center justify-between gap-2 text-[11px]" style={textStyle}>
      <span className="flex items-center gap-1.5">
        {/* Fixed width so the baseline value lines up in a scannable column
            between the "vs. PY" and "vs. NVA avg" rows despite the label's
            different lengths. truncate rather than overflow if a caller ever
            passes a longer label. */}
        <span className={`shrink-0 truncate ${compactLabel ? "w-[30px]" : "w-[68px]"}`}>
          {label}
        </span>
        {/* nowrap: "7.6 h" was breaking between the figure and its unit as soon
            as the row was squeezed, giving a two-line row and a stray "h". */}
        <span className="whitespace-nowrap tabular-nums">{renderValue(baseline)}</span>
      </span>
      <span className="flex shrink-0 items-center gap-1">
        <span className="whitespace-nowrap tabular-nums opacity-70">
          {sign}
          {renderValue(Math.abs(diff))}
        </span>
        <DeltaPill pct={pct} invert={invert} />
      </span>
    </div>
  );
}
